import React from 'react'
import { Card, CardContent, Typography } from '@mui/material'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined'
import { grey, shade } from '../../../../colors'
import { styled } from '@mui/system'
import theme from '../../../../theme'
import { formatReportReleaseTiming } from '../helper'
import {
  INSTRUCTOR_GRADE_POSTING_RELEASE_MESSAGE,
  isInstructorGradePostingReleaseMessage
} from '../../../../consts/messages'

export interface SurveyItemResponsePlaceHolderProps {
  title: string
  releaseTimeStamp?: string
  useCommentsExportMessage?: boolean
  onCommentsExportClick?: () => void
}

interface ResultsAvailableProps {
  releaseTimeStamp: string
}

const StyledBox = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  margin: `${theme.spacing(2)} ${theme.spacing(4)}`,
  padding: `${theme.spacing(6)} ${theme.spacing(4)}`,
  backgroundColor: shade[100],
  borderRadius: `${theme.spacing(2.5)}`
}))

const ResultsAvailable = ({ releaseTimeStamp }: ResultsAvailableProps): JSX.Element => {
  if (isInstructorGradePostingReleaseMessage(releaseTimeStamp)) {
    return (
      <>
        <AccessTimeOutlinedIcon />
        <Typography
          variant="body1"
          sx={{
            paddingLeft: `${theme.spacing(2.5)}`
          }}
        >
          {INSTRUCTOR_GRADE_POSTING_RELEASE_MESSAGE}
        </Typography>
      </>
    )
  }

  const formattedReleaseTimeStamp = formatReportReleaseTiming(releaseTimeStamp)
  const releasePreposition = formattedReleaseTimeStamp.startsWith('after ') ? '' : 'on '

  return (
    <>
      <AccessTimeOutlinedIcon />
      <Typography
        variant="body1"
        sx={{
          paddingLeft: `${theme.spacing(2.5)}`
        }}
      >
        Results are <em>currently scheduled</em> to release {releasePreposition}<b>{formattedReleaseTimeStamp}</b>.
      </Typography>
    </>
  )
}

const CommentsAvailable = (): JSX.Element => {
  return (
    <>
      <CommentOutlinedIcon />
      <Typography
        variant="body1"
        sx={{
          paddingLeft: `${theme.spacing(2.5)}`
        }}
      >
        Comments available on course report.
      </Typography>
    </>
  )
}

const CommentsExportAvailable = ({
  onClick
}: {
  onClick?: () => void
}): JSX.Element => {
  return (
    <>
      <CommentOutlinedIcon />
      <Typography
        variant="body1"
        sx={{
          paddingLeft: `${theme.spacing(2.5)}`
        }}
      >
        Export free response question results{' '}
        <Typography
          component="button"
          type="button"
          onClick={onClick}
          variant="inherit"
          sx={{
            border: 0,
            background: 'transparent',
            cursor: 'pointer',
            color: 'inherit',
            font: 'inherit',
            padding: 0,
            textDecoration: 'underline',
            '&:hover': {
              color: grey[700]
            }
          }}
        >
          here
        </Typography>
        .
      </Typography>
    </>
  )
}

const SurveyItemResponsePlaceHolder = ({
  title,
  releaseTimeStamp,
  useCommentsExportMessage = false,
  onCommentsExportClick
}: SurveyItemResponsePlaceHolderProps): JSX.Element => {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography color={grey[700]} variant="h6">
          {title}
        </Typography>
        <StyledBox>
          {
            (releaseTimeStamp != null)
              ? <ResultsAvailable releaseTimeStamp={releaseTimeStamp} />
              : useCommentsExportMessage
                ? (
                  <CommentsExportAvailable
                    onClick={onCommentsExportClick}
                  />
                  )
                : <CommentsAvailable />
          }
        </StyledBox>
      </CardContent>
    </Card>
  )
}

export default SurveyItemResponsePlaceHolder
